import { useQueryClient } from '@tanstack/react-query';
import Constants from 'expo-constants';
import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

import { customerStateQueryOptions } from '@/api/customer/customerStateQuery';
import type { Appointment } from '@/api/customer/customerStateQuery/types';
import {
  AppStoreReviewPrompt,
  type AppStoreReviewPromptRef,
} from '@/components/_shared/AppStoreReviewPrompt';
import { storage, storageKeys } from '@/constants/storage';
import { useLaunchDarklyFlag } from '@/hooks/flags/useLaunchDarklyFlag';
import { getApiPlatform, isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import { openSupportEmail } from '@/utils/openSupportEmail';
import { requestAppStoreReview } from '@/utils/requestAppStoreReview';
import { Tracker } from '@/utils/tracking';

import { evaluateAppStoreReviewEligibility } from './eligibility';

const FEEDBACK_RECIPIENT = 'iosfeedback@getsquire.com';
const FEEDBACK_SUBJECT = 'Feedback for Squire™';

/**
 * Lifecycle of the latest review check. `prompted` means the interstitial or
 * the native sheet fired and stays set until the next check, so competing
 * prompts (e.g. NPS) can stay suppressed for that booking.
 */
export type AppStoreReviewCheckStatus = 'idle' | 'checking' | 'prompted';

interface AppStoreReviewContextType {
  /**
   * Evaluates review eligibility for the customer and, when eligible, either
   * shows the first-time interstitial or fires the native review prompt
   * directly. Progress is exposed through `reviewCheckStatus`.
   */
  triggerAppStoreReviewCheck: (customerId: string) => Promise<void>;
  /** Dev-only: force-show the interstitial, bypassing eligibility. */
  triggerAppStoreReviewPreview: () => void;
  reviewCheckStatus: AppStoreReviewCheckStatus;
}

const AppStoreReviewContext = createContext<
  AppStoreReviewContextType | undefined
>(undefined);

export interface AppStoreReviewProviderProps {
  children: React.ReactNode;
}

export function AppStoreReviewProvider({
  children,
}: AppStoreReviewProviderProps) {
  const queryClient = useQueryClient();
  const successfulBookingsInterstitial = useLaunchDarklyFlag<number>(
    'successfulBookingsInterstitial',
    2,
  );
  const successfulBookingsInAppReview = useLaunchDarklyFlag<number>(
    'successfulBookingsInAppReview',
    3,
  );

  const promptRef = useRef<AppStoreReviewPromptRef>(null);
  const [reviewCheckStatus, setReviewCheckStatus] =
    useState<AppStoreReviewCheckStatus>('idle');

  const triggerAppStoreReviewCheck = useCallback(
    async (customerId: string): Promise<void> => {
      if (isWeb() || !customerId) {
        return;
      }

      setReviewCheckStatus('checking');

      let appointments: Appointment[] = [];
      try {
        const state = await queryClient.fetchQuery(
          customerStateQueryOptions(customerId),
        );
        appointments = [
          ...(state.upcomingAppointments ?? []),
          ...(state.pastAppointments ?? []),
        ];
      } catch (error) {
        Logger.error('[AppStoreReview] failed to load customer state', {
          error,
        });
        setReviewCheckStatus('idle');
        return;
      }

      const hasSeenInterstitial =
        storage.getBoolean(storageKeys.hasSeenReviewInterstitial) ?? false;
      const lastSuccessfulAppointmentCount = storage.getNumber(
        storageKeys.lastSuccessfulAppointmentCount,
      );

      const { shouldPrompt, successfulAppointments } =
        evaluateAppStoreReviewEligibility({
          appointments,
          lastSuccessfulAppointmentCount,
          successfulBookingsInterstitial,
          successfulBookingsInAppReview,
        });

      if (__DEV__) {
        Logger.console('[AppStoreReview] eligibility check', {
          customerId,
          successfulAppointments,
          lastSuccessfulAppointmentCount,
          hasSeenInterstitial,
          shouldPrompt,
        });
      }

      if (!shouldPrompt) {
        setReviewCheckStatus('idle');
        return;
      }

      storage.set(
        storageKeys.lastSuccessfulAppointmentCount,
        successfulAppointments,
      );
      setReviewCheckStatus('prompted');

      if (hasSeenInterstitial) {
        void requestAppStoreReview();
        return;
      }

      promptRef.current?.open();
      Tracker.track('in_app_review.request_in_app_review');
    },
    [
      queryClient,
      successfulBookingsInterstitial,
      successfulBookingsInAppReview,
    ],
  );

  const triggerAppStoreReviewPreview = useCallback(() => {
    Logger.console(
      '[AppStoreReview] preview triggered — bypassing eligibility',
    );
    promptRef.current?.open();
    Tracker.track('in_app_review.request_in_app_review');
  }, []);

  const handleLoveIt = useCallback(() => {
    storage.set(storageKeys.hasSeenReviewInterstitial, true);
    void requestAppStoreReview();
  }, []);

  const handleNeedsWork = useCallback(() => {
    const appVersion =
      Constants.expoConfig?.extra?.EXPO_APP_VERSION ||
      Constants.expoConfig?.version ||
      '';
    const body = `\n\nPlatform: ${getApiPlatform()}\nVersion: ${appVersion}`;

    openSupportEmail({
      recipientEmail: FEEDBACK_RECIPIENT,
      subject: FEEDBACK_SUBJECT,
      body,
    }).catch((error) => {
      Logger.error('[AppStoreReview] failed to open feedback email', { error });
    });
  }, []);

  const value: AppStoreReviewContextType = {
    triggerAppStoreReviewCheck,
    triggerAppStoreReviewPreview,
    reviewCheckStatus,
  };

  return (
    <AppStoreReviewContext.Provider value={value}>
      {children}
      <AppStoreReviewPrompt
        ref={promptRef}
        onLoveIt={handleLoveIt}
        onNeedsWork={handleNeedsWork}
      />
    </AppStoreReviewContext.Provider>
  );
}

export function useAppStoreReviewContext(): AppStoreReviewContextType {
  const context = useContext(AppStoreReviewContext);
  if (!context) {
    throw new Error(
      'useAppStoreReviewContext must be used within AppStoreReviewProvider',
    );
  }
  return context;
}
